import React, { Component, ErrorInfo, ReactNode } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { RefreshCcw, AlertTriangle, Bug, Home, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useReducedMotion } from '@/hooks/useReducedMotion';

interface Props {
  children: ReactNode;
  fallback?: ComponentType<ErrorFallbackProps>;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  enableErrorReporting?: boolean;
  showErrorDetails?: boolean;
  maxRetries?: number;
  retryDelay?: number;
  isolateError?: boolean;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  retryCount: number;
  errorId: string;
  isRetrying: boolean;
  errorHistory: ErrorRecord[];
}

interface ErrorRecord {
  id: string;
  error: Error;
  timestamp: number;
  componentStack: string;
  userAgent: string;
  url: string;
}

interface ErrorFallbackProps {
  error: Error;
  errorInfo: ErrorInfo | null;
  resetError: () => void;
  retryCount: number;
  maxRetries: number;
  errorId: string;
  isRetrying: boolean;
  onReportError: () => void;
  errorHistory: ErrorRecord[];
}

type ComponentType<P = Record<string, unknown>> = React.ComponentType<P>;

class AdvancedErrorBoundary extends Component<Props, State> {
  private retryTimeout: NodeJS.Timeout | null = null;

  constructor(props: Props) {
    super(props);

    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
      errorId: '',
      isRetrying: false,
      errorHistory: this.loadErrorHistory(),
    };
  }

  private loadErrorHistory(): ErrorRecord[] {
    try {
      const stored = localStorage.getItem('error-boundary-history');
      return stored ? JSON.parse(stored).slice(-10) : []; // Keep last 10 errors
    } catch {
      return [];
    }
  }

  private saveErrorHistory(errorRecord: ErrorRecord): void {
    try {
      const history = [...this.state.errorHistory, errorRecord].slice(-10);
      localStorage.setItem('error-boundary-history', JSON.stringify(history));
      this.setState({ errorHistory: history });
    } catch (error) {
      console.warn('Failed to save error history:', error);
    }
  }

  private generateErrorId(): string {
    return `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
      errorId: `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const errorId = this.generateErrorId();
    const errorRecord: ErrorRecord = {
      id: errorId,
      error,
      timestamp: Date.now(),
      componentStack: errorInfo.componentStack,
      userAgent: navigator.userAgent,
      url: window.location.href,
    };

    this.setState({
      errorInfo,
      errorId,
    });

    this.saveErrorHistory(errorRecord);

    // Call custom error handler
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Report to error service
    if (this.props.enableErrorReporting) {
      this.reportError(errorRecord);
    }

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Error Boundary caught an error:', error, errorInfo);
    }
  }

  private async reportError(errorRecord: ErrorRecord): Promise<void> {
    try {
      // Report to Sentry if available
      if (typeof window !== 'undefined' && (window as any).Sentry) {
        (window as any).Sentry.captureException(errorRecord.error, {
          tags: { errorBoundary: true },
          extra: {
            componentStack: errorRecord.componentStack,
            errorId: errorRecord.id,
          },
        });
      }

      // Report to custom endpoint
      await fetch('/api/errors', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...errorRecord,
          error: {
            message: errorRecord.error.message,
            stack: errorRecord.error.stack,
            name: errorRecord.error.name,
          },
        }),
      });
    } catch (reportingError) {
      console.warn('Failed to report error:', reportingError);
    }
  }

  private resetError = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: '',
      isRetrying: false,
    });
  };

  private retryWithDelay = (): void => {
    const { maxRetries = 3, retryDelay = 1000 } = this.props;
    const { retryCount } = this.state;

    if (retryCount >= maxRetries) {
      return;
    }

    this.setState({ isRetrying: true });

    this.retryTimeout = setTimeout(() => {
      this.setState(prevState => ({
        hasError: false,
        error: null,
        errorInfo: null,
        errorId: '',
        isRetrying: false,
        retryCount: prevState.retryCount + 1,
      }));
    }, retryDelay);
  };

  componentWillUnmount(): void {
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
    }
  }

  render(): ReactNode {
    const { hasError, error, errorInfo, retryCount, errorId, isRetrying, errorHistory } =
      this.state;
    const {
      children,
      fallback: CustomFallback,
      maxRetries = 3,
      showErrorDetails = false,
    } = this.props;

    if (hasError && error) {
      const fallbackProps: ErrorFallbackProps = {
        error,
        errorInfo,
        resetError: this.resetError,
        retryCount,
        maxRetries,
        errorId,
        isRetrying,
        onReportError: () =>
          this.reportError({
            id: errorId,
            error,
            timestamp: Date.now(),
            componentStack: errorInfo?.componentStack || '',
            userAgent: navigator.userAgent,
            url: window.location.href,
          }),
        errorHistory,
      };

      if (CustomFallback) {
        return <CustomFallback {...fallbackProps} />;
      }

      return <DefaultErrorFallback {...fallbackProps} showErrorDetails={showErrorDetails} />;
    }

    return children;
  }
}

// Default error fallback component
interface DefaultErrorFallbackProps extends ErrorFallbackProps {
  showErrorDetails?: boolean;
}

const DefaultErrorFallback: React.FC<DefaultErrorFallbackProps> = ({
  error,
  errorInfo,
  resetError,
  retryCount,
  maxRetries,
  errorId,
  isRetrying,
  onReportError,
  errorHistory,
  showErrorDetails = false,
}) => {
  const canRetry = retryCount < maxRetries;
  const [showDetails, setShowDetails] = React.useState(showErrorDetails);
  const [showHistory, setShowHistory] = React.useState(false);
  const prefersReducedMotion = useReducedMotion();

  return (
    <div className="min-h-[400px] flex items-center justify-center p-4">
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: prefersReducedMotion ? 0 : 0.3 }}
        >
          <Card className="w-full max-w-[calc(100vw-2rem)] sm:max-w-lg md:max-w-2xl">
            <CardHeader className="text-center">
              <motion.div
                initial={{ rotate: 0 }}
                animate={{ rotate: isRetrying && !prefersReducedMotion ? 360 : 0 }}
                transition={{
                  duration: 1,
                  repeat: isRetrying && !prefersReducedMotion ? Infinity : 0,
                }}
                className="mx-auto mb-4 p-3 bg-red-100 rounded-full w-fit"
              >
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </motion.div>

              <CardTitle className="text-2xl text-red-900">Something went wrong</CardTitle>

              <CardDescription className="text-base">
                An unexpected error occurred while loading this component.
                {retryCount > 0 && ` (Attempt ${retryCount + 1}/${maxRetries + 1})`}
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-6">
              {/* Error Summary */}
              <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                <h3 className="font-semibold text-red-900 mb-2">Error Details</h3>
                <p className="text-red-800 font-mono text-xs overflow-x-auto whitespace-pre-wrap break-words">
                  {error.message || 'Unknown error occurred'}
                </p>
                <p className="text-red-600 text-xs mt-1">Error ID: {errorId}</p>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <Button onClick={resetError} className="flex-1" disabled={isRetrying}>
                  {isRetrying ? (
                    <>
                      <RefreshCcw className="w-4 h-4 mr-2 animate-spin" />
                      Retrying...
                    </>
                  ) : (
                    <>
                      <RefreshCcw className="w-4 h-4 mr-2" />
                      Try Again
                    </>
                  )}
                </Button>

                <Button
                  variant="outline"
                  onClick={() => (window.location.href = '/')}
                  className="flex-1"
                >
                  <Home className="w-4 h-4 mr-2" />
                  Go Home
                </Button>

                <Button variant="outline" onClick={() => window.history.back()}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Go Back
                </Button>
              </div>

              {/* Advanced Options */}
              <div className="space-y-3">
                <div className="flex flex-wrap gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setShowDetails(!showDetails)}>
                    <Bug className="w-4 h-4 mr-2" />
                    {showDetails ? 'Hide' : 'Show'} Technical Details
                  </Button>

                  <Button variant="ghost" size="sm" onClick={onReportError}>
                    Send Error Report
                  </Button>

                  {errorHistory.length > 0 && (
                    <Button variant="ghost" size="sm" onClick={() => setShowHistory(!showHistory)}>
                      Error History ({errorHistory.length})
                    </Button>
                  )}
                </div>

                {/* Technical Details */}
                <AnimatePresence>
                  {showDetails && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: prefersReducedMotion ? 0 : 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="p-4 bg-stone-50 rounded-lg border">
                        <h4 className="font-semibold mb-2">Stack Trace</h4>
                        <pre className="text-xs overflow-auto max-h-40 font-mono text-stone-700">
                          {error.stack}
                        </pre>

                        {errorInfo?.componentStack && (
                          <>
                            <h4 className="font-semibold mb-2 mt-4">Component Stack</h4>
                            <pre className="text-xs overflow-auto max-h-40 font-mono text-stone-700">
                              {errorInfo.componentStack}
                            </pre>
                          </>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Error History */}
                <AnimatePresence>
                  {showHistory && errorHistory.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: prefersReducedMotion ? 0 : 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="p-4 bg-stone-50 rounded-lg border">
                        <h4 className="font-semibold mb-2">Recent Errors</h4>
                        <div className="space-y-2 max-h-40 overflow-auto">
                          {errorHistory
                            .slice(-5)
                            .reverse()
                            .map(record => (
                              <div
                                key={record.id}
                                className="text-sm border-l-2 border-red-200 pl-3"
                              >
                                <p className="font-mono text-red-800">{record.error.message}</p>
                                <p className="text-stone-500 text-xs">
                                  {new Date(record.timestamp).toLocaleString()}
                                </p>
                              </div>
                            ))}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Retry Information */}
              {!canRetry && (
                <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                  <p className="text-yellow-800 text-sm">
                    Maximum retry attempts reached. Please refresh the page or contact support if
                    the problem persists.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default AdvancedErrorBoundary;
export { AdvancedErrorBoundary, DefaultErrorFallback };
export type { Props as ErrorBoundaryProps, ErrorFallbackProps };
